import { axios } from '@ben/api/axios';
import { Combobox } from '@ben/orion/combobox';
import { Loader, TextInput, useCombobox } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

interface Employer {
  id: number;
  name: string;
}

const PAGE_SIZE = 20;

interface EmployersPage {
  count: number;
  results: Employer[];
}

const getEmployers = async (search: string): Promise<Employer[]> => {
  const response = await axios.get<EmployersPage>('employers/', {
    params: { page: 1, pageSize: PAGE_SIZE, ...(search ? { search } : {}) },
  });
  return response.data.results;
};

interface EmployerComboboxProps {
  selectedId?: number;
  /** Stored alongside the ID in the URL so the label survives navigation
   * without a separate fetch. */
  selectedName?: string;
  onChange: (id: number, name: string) => void;
}

export const EmployerCombobox = ({
  selectedId,
  selectedName,
  onChange,
}: EmployerComboboxProps) => {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
  });

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);

  const { data: employers = [], isFetching } = useQuery({
    queryKey: ['employers', debouncedSearch],
    queryFn: () => getEmployers(debouncedSearch),
  });

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(value: string) => {
        const employer = employers.find((e) => String(e.id) === value);
        if (employer) onChange(employer.id, employer.name);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <TextInput
          label="Employer"
          placeholder="Search employers…"
          value={combobox.dropdownOpened ? search : (selectedName ?? '')}
          onChange={(e) => {
            setSearch(e.target.value);
            combobox.openDropdown();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          rightSection={
            isFetching ? <Loader size="xs" /> : <Combobox.Chevron />
          }
          rightSectionPointerEvents="none"
          w={320}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {employers.length > 0 ? (
            employers.map((employer) => (
              <Combobox.Option
                key={employer.id}
                value={String(employer.id)}
                active={employer.id === selectedId}
              >
                {employer.name}
              </Combobox.Option>
            ))
          ) : (
            <Combobox.Empty>
              {isFetching ? 'Loading…' : 'No employers found'}
            </Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
